import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Twitter, Facebook, Instagram, Linkedin, Mail, MessageCircle, LifeBuoy } from "lucide-react";
import Logo from "./Logo";
import { useCMS, useSectionEnabled } from "../context/CMSContext";
import { listPages } from "../lib/api";

// Build a `wa.me` link from a raw phone number (strips +, spaces, dashes, parens).
const buildWaUrl = (raw, prefill) => {
  const digits = String(raw || "").replace(/[^\d]/g, "");
  if (!digits) return "";
  const text = prefill ? `?text=${encodeURIComponent(prefill)}` : "";
  return `https://wa.me/${digits}${text}`;
};

// Float-right WhatsApp bubble. Renders only when a number is configured.
function WhatsAppFloat({ number, brand }) {
  const url = buildWaUrl(number, `Hi ${brand}, I need help with`);
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-float-cta"
      className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 px-4 py-3 rounded-full bg-[#25D366] text-white text-sm font-bold shadow-lg shadow-black/20 hover:scale-105 transition-transform"
      aria-label="Chat on WhatsApp"
    >
      <MessageCircle className="w-5 h-5" /> WhatsApp us
    </a>
  );
}

export default function Footer() {
  const enabled = useSectionEnabled("footer");
  const { cms } = useCMS();
  const [pages, setPages] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  useEffect(() => {
    listPages().then(setPages).catch(() => setPages([]));
  }, []);

  const handleLink = (e, href) => {
    if (!href || !href.startsWith("#")) return;
    e.preventDefault();
    if (onHome) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/" + href);
    }
  };

  if (!enabled) return null;
  const footer = cms?.footer || {};
  const site = cms?.site || {};
  const brand = site.brandName || "Live Mover";
  const email = site.supportEmail || "hello@livemover.com";
  const whatsapp = (footer.whatsapp_number || site.whatsapp_number || "").trim();
  const supportTicketUrl = (footer.support_ticket_url || "").trim();
  const showEmail = !!footer.show_email;
  const socials = footer.socials || {};
  const icons = { twitter: Twitter, facebook: Facebook, instagram: Instagram, linkedin: Linkedin };

  // ----- Build link columns -----
  // Group dynamic CMS pages by their footer_column field
  const dynamicByColumn = {};
  for (const p of pages) {
    if (p.show_in_footer && p.footer_column) {
      (dynamicByColumn[p.footer_column] ||= []).push(p);
    }
  }

  // Normaliser used for de-duplication. "Privacy" === "Privacy Policy"-stripped
  // is intentionally NOT done — only exact (case + trim) label match collapses.
  const norm = (s) => String(s || "").trim().toLowerCase();

  const mergedColumnsRaw = (footer.columns || []).map((c) => {
    const extras = (dynamicByColumn[c.title] || [])
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((p) => ({ label: p.title, href: `/${p.slug}`, internal: true }));
    const staticLinks = (c.links || []).map((l) =>
      typeof l === "string" ? { label: l, href: "#footer" } : l,
    );
    // De-dup by label (case-insensitive). Static links win over auto-pages.
    const seen = new Set();
    const merged = [];
    for (const l of [...staticLinks, ...extras]) {
      const key = norm(l.label);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      merged.push(l);
    }
    return { title: c.title, links: merged };
  });

  // Limit to the first 3 columns so the layout stays clean. Admin can re-order
  // in the Footer tab to choose which 3 ship to the live site.
  const visibleColumns = mergedColumnsRaw.slice(0, 3);

  return (
    <>
      <footer className="bg-[#0B0E13] text-white pt-14 sm:pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Brand block on top (full width on mobile, sits inline with cols on lg) */}
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="flex items-center gap-2">
                <Logo size={36} dark />
                <span className="lm-font font-extrabold text-xl">{brand}</span>
              </div>
              <p className="text-white/60 text-sm mt-4 max-w-xs leading-relaxed">{footer.tagline}</p>

              {/* Instant Support block — single row of cards (WhatsApp · Email* · Ticket) */}
              {(whatsapp || supportTicketUrl || (showEmail && email)) && (
                <div className="mt-6" data-testid="footer-instant-support">
                  <h4 className="lm-font font-bold text-white text-xs uppercase tracking-wider mb-3 inline-flex items-center gap-2">
                    <LifeBuoy className="w-3.5 h-3.5 text-[#FF6B3D]" /> Instant Support
                  </h4>
                  <div className="flex flex-wrap items-stretch gap-2">
                    {whatsapp && (
                      <a
                        href={buildWaUrl(whatsapp, `Hi ${brand}, I need help with`)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-whatsapp-link"
                        className="group flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 hover:bg-[#25D366]/15 border border-white/10 hover:border-[#25D366]/50 transition-colors"
                      >
                        <span className="w-7 h-7 rounded-full bg-[#25D366]/20 flex items-center justify-center">
                          <MessageCircle className="w-4 h-4 text-[#25D366]" />
                        </span>
                        <span className="leading-tight">
                          <span className="block text-[10px] uppercase tracking-wider text-white/45 font-semibold">WhatsApp</span>
                          <span className="block text-xs font-bold text-white group-hover:text-[#25D366]">{whatsapp}</span>
                        </span>
                      </a>
                    )}
                    {supportTicketUrl && (
                      <a
                        href={supportTicketUrl}
                        target={/^https?:/i.test(supportTicketUrl) ? "_blank" : undefined}
                        rel={/^https?:/i.test(supportTicketUrl) ? "noopener noreferrer" : undefined}
                        data-testid="footer-ticket-link"
                        className="group flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 hover:bg-[#FF6B3D]/15 border border-white/10 hover:border-[#FF6B3D]/50 transition-colors"
                      >
                        <span className="w-7 h-7 rounded-full bg-[#FF6B3D]/20 flex items-center justify-center">
                          <LifeBuoy className="w-4 h-4 text-[#FF6B3D]" />
                        </span>
                        <span className="leading-tight">
                          <span className="block text-[10px] uppercase tracking-wider text-white/45 font-semibold">Ticket</span>
                          <span className="block text-xs font-bold text-white group-hover:text-[#FF6B3D]">Submit a ticket</span>
                        </span>
                      </a>
                    )}
                    {showEmail && email && (
                      <a
                        href={`mailto:${email}`}
                        data-testid="footer-email-link"
                        className="group flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 hover:bg-[#FF6B3D]/15 border border-white/10 hover:border-[#FF6B3D]/50 transition-colors"
                      >
                        <span className="w-7 h-7 rounded-full bg-[#FF6B3D]/20 flex items-center justify-center">
                          <Mail className="w-4 h-4 text-[#FF6B3D]" />
                        </span>
                        <span className="leading-tight">
                          <span className="block text-[10px] uppercase tracking-wider text-white/45 font-semibold">Email</span>
                          <span className="block text-xs font-bold text-white group-hover:text-[#FF6B3D] break-all">{email}</span>
                        </span>
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Socials */}
              <div className="flex items-center gap-3 mt-5">
                {Object.entries(socials).map(([k, url]) => {
                  const Icon = icons[k];
                  if (!Icon) return null;
                  const raw = (url || "").trim();
                  if (!raw || raw === "#") return null;
                  return (
                    <a
                      key={k}
                      href={raw}
                      target={/^https?:/i.test(raw) ? "_blank" : undefined}
                      rel={/^https?:/i.test(raw) ? "noopener noreferrer" : undefined}
                      className="w-9 h-9 rounded-full bg-white/5 hover:bg-[#FF6B3D] hover:text-[#11151C] flex items-center justify-center transition-colors"
                    >
                      <Icon className="w-4 h-4" />
                    </a>
                  );
                })}
              </div>
            </div>

            {/* 3 link columns (always 3 on lg, 2 on md, 1 on mobile) */}
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {visibleColumns.map((c) => (
                <div key={c.title}>
                  <h4 className="lm-font font-bold text-white text-sm uppercase tracking-wider mb-4">{c.title}</h4>
                  <ul className="space-y-2.5">
                    {c.links.map((l, i) => {
                      const linkClass = "text-white/60 hover:text-[#FF6B3D] text-sm transition-colors";
                      if (l.internal) {
                        return (
                          <li key={`${l.label}-${i}`}>
                            <Link to={l.href} className={linkClass}>{l.label}</Link>
                          </li>
                        );
                      }
                      const raw = (l.href || "").trim();
                      if (!raw || raw === "#") {
                        return (
                          <li key={`${l.label}-${i}`}>
                            <span className="text-white/40 text-sm cursor-default">{l.label}</span>
                          </li>
                        );
                      }
                      if (/^(https?:|mailto:|tel:)/i.test(raw)) {
                        return (
                          <li key={`${l.label}-${i}`}>
                            <a
                              href={raw}
                              target={/^https?:/i.test(raw) ? "_blank" : undefined}
                              rel={/^https?:/i.test(raw) ? "noopener noreferrer" : undefined}
                              className={linkClass}
                            >
                              {l.label}
                            </a>
                          </li>
                        );
                      }
                      if (raw.startsWith("/")) {
                        return (
                          <li key={`${l.label}-${i}`}>
                            <Link to={raw} className={linkClass}>{l.label}</Link>
                          </li>
                        );
                      }
                      return (
                        <li key={`${l.label}-${i}`}>
                          <a
                            href={raw.startsWith("#") && !onHome ? `/${raw}` : raw}
                            onClick={(e) => handleLink(e, raw)}
                            className={linkClass}
                          >
                            {l.label}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-white/50 text-xs">
              &copy; {new Date().getFullYear()} {brand}. All rights reserved.
            </p>
            <div className="flex items-center gap-2 text-white/50 text-xs">
              <Mail className="w-3.5 h-3.5" /> {email}
            </div>
          </div>
        </div>
      </footer>

      <WhatsAppFloat number={whatsapp} brand={brand} />
    </>
  );
}
